// @ts-strict-ignore
import { Icon } from '@blueprintjs/core'
import { Classes, Popover } from './ui/popover'
import { Tooltip } from './ui/tooltip'
import classNames from 'classnames'
import { differenceInCalendarDays, differenceInCalendarMonths, parse } from 'date-fns'
import { Dictionary } from 'lodash'
import keyBy from 'lodash.keyby'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DefaultNumericOperators } from '../config'
import { Operator, FilterI as OriginalFilter } from '../constants/filter'

interface FilterTagsProps {
    loading: boolean
    filters: Dictionary<OriginalFilter>
    activeFilters: ActiveFilters
    setFiltersMenuState: (state: any) => void
    removeActiveFilter: (key: FilterKeyName) => any
    // additionalFilterData: any
    isFilterMenuOpen: boolean
    maxWidth?: number
}

export type ActiveFilters = {
    [key in FilterKeyName]?: number | string | SelectComponentFilter
}

type SelectComponentFilter = {
    name: string
    type: string
}

export type FilterKeyName =
    | 'portfolio_id'
    | 'campaign_id'
    | 'state'
    | 'targeting_type'
    | 'default_bid'
    | 'target_acos'
    | 'cost'
    | 'sales'
    | 'acos'
    | 'roas'
    | 'impressions'
    | 'clicks'
    | 'ctr'
    | 'rpc'
    | 'cpc'
    | 'orders'
    | 'cvr'
    | 'cac'
    | 'aov'
    | 'has_promotions'
    | 'ad_group_id'
    | 'start_date'
    | 'end_date'

// it's a good idea for this to be used memoized
export const FilterTags = ({
    loading,
    filters,
    activeFilters,
    removeActiveFilter,
    setFiltersMenuState,
    // additionalFilterData,
    isFilterMenuOpen,
    maxWidth = 435,
}: FilterTagsProps) => {
    const divRef = useRef<HTMLDivElement>(null)

    const [hiddenTagsCount, setHiddenTagsCount] = useState<number>(0)

    const _removeActiveFilter = (activeFilter: FilterKeyName) => {
        // purposefully left to refresh the tags list
        setHiddenTagsCount(0)
        removeActiveFilter(activeFilter)
    }

    const totalFiltersCount = useMemo(() => Object.keys(activeFilters).length, [activeFilters])

    const moreFilterPopup = useMemo(() => {
        const filterTags = (
            <ul>
                {(Object.keys(activeFilters) as FilterKeyName[])
                    .slice(totalFiltersCount - hiddenTagsCount)
                    .map((filterKeyName, index) => (
                        <li className="my-1.5" key={index}>
                            {renderTag(
                                filters,
                                // additionalFilterData,
                                filterKeyName,
                                filters[filterKeyName]?.name ?? filterKeyName,
                                activeFilters,
                                _removeActiveFilter,
                                loading,
                            )}
                        </li>
                    ))}
            </ul>
        )

        if (totalFiltersCount > totalFiltersCount - hiddenTagsCount) {
            return (
                <Popover
                    popoverClassName={Classes.POPOVER2_CONTENT_SIZING}
                    content={filterTags}
                    interactionKind="click"
                    position="bottom"
                >
                    <span className="underline text-xs">
                        +{hiddenTagsCount} more {hiddenTagsCount == 1 ? 'filter' : 'filters'}
                    </span>
                </Popover>
            )
        }
    }, [totalFiltersCount, hiddenTagsCount, activeFilters, loading])

    const generateTag = ({ filterKeyName }: { filterKeyName: FilterKeyName }) => {
        return renderTag(
            filters,
            // additionalFilterData,
            filterKeyName,
            filters[filterKeyName]?.name ?? filterKeyName,
            activeFilters,
            _removeActiveFilter,
            loading,
            setFiltersMenuState,
            isFilterMenuOpen,
        )
    }

    // manages the show/hide state of the tags
    useEffect(() => {
        const div = divRef.current
        if (!div) {
            return
        }

        const containerWidth = Math.max(div.clientWidth, maxWidth)
        const tagElements = div.querySelectorAll('._unique_filter_tags')
        let hiddenCount = 0
        /** @ts-ignore */
        for (const tagElement of tagElements) {
            const parentRect = div.getBoundingClientRect() // get parent position
            const tagRect = tagElement.getBoundingClientRect() // get child position

            if (tagRect.right - parentRect.left + 85 > containerWidth) {
                hiddenCount = tagElements.length - parseInt(tagElement.dataset.tagIndex, 10)
                console.log('container gets here', hiddenCount)
                break
            }
        }

        setHiddenTagsCount(hiddenCount)
    }, [activeFilters])

    return (
        <div className="flex">
            <div ref={divRef} className="flex overflow-hidden" style={{ maxWidth: maxWidth }}>
                {(Object.keys(activeFilters) as FilterKeyName[]).map(
                    (filterKeyName: FilterKeyName, index) => {
                        const shouldHide =
                            index >= Object.keys(activeFilters).length - hiddenTagsCount
                        return (
                            <div
                                className={classNames('_unique_filter_tags', {
                                    hidden: shouldHide,
                                })}
                                data-tag-index={index}
                                key={index}
                            >
                                {generateTag({ filterKeyName })}
                            </div>
                        )
                    },
                )}
                {moreFilterPopup}
            </div>
        </div>
    )
}

const renderTag = (
    filters: Dictionary<OriginalFilter>,
    // additionalFilterData: any,
    filterKeyName: FilterKeyName,
    filterName: string,
    activeFilters: ActiveFilters,
    removeActiveFilter: (v: FilterKeyName) => any,
    loading = false,
    setFiltersMenuState: ((state: any) => void) | null = null,
    isFilterMenuStateOpen = false,
) => {
    const value = activeFilters[filterKeyName]
    const filter = filters[filterKeyName]
    const resolvedValue = resolveValue(value, filter)

    const toggleParentOpen = () => {
        if (!!setFiltersMenuState) {
            setFiltersMenuState(null)
        }
    }

    const tag = (verbiage: string, index: number) => (
        <span
            key={index}
            className="bp4-tag bp4-minimal bp4-interactive bg-white border border-mjDark-600 mr-2"
        >
            <span
                onMouseDown={() => (!isFilterMenuStateOpen ? toggleParentOpen() : null)}
                className="bp4-fill bp4-text-overflow-ellipsis text-mjDark-800 text-xs font-normal"
            >
                {verbiage}
            </span>
            <button
                disabled={loading}
                onClick={() => removeActiveFilter(filterKeyName)}
                className="bp4-tag-remove"
            >
                <Icon icon="small-cross" color="#2B4E75" />
            </button>
        </span>
    )

    if (typeof value == 'string' || typeof value == 'number') {
        return tag(`${filterName}: ${resolvedValue}`, 0)
    } else if (typeof value == 'object') {
        // we use this for filters with operators i.e {">": 5}
        if (value.type == 'select-component') {
            const name = value?.name?.length > 13 ? value.name.slice(0, 10) + '...' : value?.name
            if (value?.name?.length > 13) {
                return (
                    <span>
                        <Tooltip key={0} position="top" content={value?.name ?? 'undefined'}>
                            {tag(`${filterName} = ${name}`, 0)}
                        </Tooltip>
                    </span>
                )
            }

            return tag(`${filterName} = ${name}`, 0)
        }

        // Some filters may support multiple operators,
        // so the loop below handles the various operators available.
        const final = []
        Object.keys(value).forEach((operator, index) => {
            // tThis gets used for 95% of the filters
            if (DefaultNumericOperators.includes(operator as Operator)) {
                final.push(tag(`${filterName} ${operator} ${value[operator]}`, index))
                return
            }

            let output = null
            let v = activeFilters[filterKeyName][operator]
            const period =
                filterKeyName == 'start_date' || filterKeyName == 'end_date'
                    ? activeFilters[filterKeyName].period
                    : null

            console.log('[filterTags]', {
                v,
                period,
                value,
            })

            if (v == 'select-component') {
                const t = value[operator]
                const val = t.length > 13 ? t.slice(0, 10) + '...' : t
                final.push(tag(`${filterName} = ${val}`, index))
                return
            }

            let overrideOperator
            if (operator == 'last') {
                overrideOperator = 'is in the last'
                const z = parse(v, 'yyyy-MM-dd', new Date())
                if (period == 'days') {
                    v = Math.abs(differenceInCalendarDays(z, new Date()))
                } else if (period == 'months') {
                    v = Math.abs(differenceInCalendarMonths(z, new Date()))
                }
            } else if (operator == 'period') {
                return
            }

            const suffix = operator == 'last' ? period : ''
            if (overrideOperator != '') {
                output = tag(`${filterName} ${overrideOperator} ${v} ${suffix}`, index)
            } else {
                const name =
                    value?.name?.length > 13 ? value.name.slice(0, 10) + '...' : value?.name
                output = tag(`${filterName}: ${name ?? 'undefined'}`, index)
            }

            if (value?.name?.length > 13) {
                final.push(
                    <Tooltip key={index} position="top" content={value?.name ?? 'undefined'}>
                        {output}
                    </Tooltip>,
                )
            } else {
                final.push(output)
            }
        })
        return final
    }
}

const resolveValue = (value, filter) => {
    if (filter.type === 'select') {
        const keyedOptions = keyBy(filter.options, 'value')
        if (keyedOptions[value]?.name) {
            return keyedOptions[value].name
        }
    }

    return `${filter?.prefix ?? ''}${value}${filter?.suffix ?? ''}`
}
